import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Download, Play } from "lucide-react";
import {
    Grid3X3,
    List
} from "lucide-react";
import { materials } from "../data/materials";
import VideoModal from "../components/VideoModal";
import { PDFDocument } from "pdf-lib";

const images = require.context(
    "../assets/images",
    false,
    /\.(png|jpe?g|svg)$/
);

const imageMap = images.keys().reduce((acc, path) => {
    const key = path.replace("./", "").replace(/\.(png|jpe?g|svg)$/, "");
    acc[key] = images(path);
    return acc;
}, {});

const pdfs = require.context(
    "../assets/pdfs",
    false,
    /\.pdf$/
);

const pdfMap = pdfs.keys().reduce((acc, path) => {
    const key = path.replace("./", "").replace(".pdf", "");
    acc[key] = pdfs(path);
    return acc;
}, {});

const FILTERS = [
    "cheat-sheet-test",
    "hiragana",
    "katakana",
    "all",
    "single",
];

const WRITING_PAGE_META = {
    EN: {
        title: "Free Japanese Writing Worksheets | Hanapin – Hiragana & Katakana Printables",
        desc:  "Download free printable hiragana and katakana writing worksheets. Import into Goodnotes or print at home!",
    },
    JP: {
        title: "無料プリントをダウンロード | はなぴん – ひらがな・カタカナ練習プリント",
        desc:  "印刷したりiPadにダウンロードしたりして書く練習ができます！",
    },
    ZH: {
        title: "免费练习字帖 | 樱花老师",
        desc:  "可打印，也可导入 Goodnotes，在家轻松练字！",
    },
};

export default function Writing({ language, texts }) {
    const t = texts || {
        tags: {},
    };
    const [viewMode, setViewMode] = useState("grid");
    const [selectedVideo, setSelectedVideo] = useState(null);

    // ── Per-page SEO ──────────────────────────────────────────────────────────
    useEffect(() => {
        const meta = WRITING_PAGE_META[language] || WRITING_PAGE_META.EN;
        document.title = meta.title;
        const el = document.querySelector('meta[name="description"]');
        if (el) el.setAttribute("content", meta.desc);
    }, [language]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [previewItem, setPreviewItem] = useState(null);
    const [activeFilters, setActiveFilters] = useState(["cheat-sheet-test"]);
    const [searchTerm, setSearchTerm] = useState("");;

    const getFilterLabel = (key) =>
  texts.tags?.[key]

    const toggleItem = (file) => {
        console.log("toggle", file);
        setSelectedItems((prev) =>
            prev.includes(file)
                ? prev.filter((f) => f !== file)
                : [...prev, file]
        );
    };

    const toggleFilter = (value) => {
        setActiveFilters(prev =>
            prev.includes(value)
                ? prev.filter(v => v !== value)
                : [...prev, value]
        );
    };

    const buildViewMaterial = (material) => ({
        ...material,

        title: `${String(material.id).padStart(2, "0")} ${material.title}`,

        image: imageMap[material.image],

        file: pdfMap[
            material.file?.replace(".pdf", "")
        ],

        videoId: material.videoId || null,

        tags: material.tags || [],
    });

    const matchesFilters = (mat) => {
        const searchMatch = mat.title
            .toLowerCase()
            .includes(searchTerm.toLowerCase());

        const filterMatch =
            activeFilters.length === 0 ||
            activeFilters.every(f => mat.tags.includes(f));

        return searchMatch && filterMatch;
    };

    const allFiles = materials
        .map((item) => buildViewMaterial(item).file)
        .filter(Boolean);

    const selectVisible = () => {
        const files = filteredMaterials
            .map((m) => buildViewMaterial(m).file)
            .filter(Boolean);

        const allSelected = files.every((file) =>
            selectedItems.includes(file)
        );

        if (allSelected) {
            setSelectedItems((prev) =>
                prev.filter((file) => !files.includes(file))
            );
        } else {
            setSelectedItems((prev) => [
                ...new Set([...prev, ...files]),
            ]);
        }
    };


    const downloadSelected = async () => {
    if (!selectedItems.length) return;

    const mergedPdf = await PDFDocument.create();

    for (const file of selectedItems) {
        const pdfBytes = await fetch(file).then(res =>
            res.arrayBuffer()
        );

        const pdf = await PDFDocument.load(pdfBytes);

        const pages = await mergedPdf.copyPages(
            pdf,
            pdf.getPageIndices()
        );

        pages.forEach(page => mergedPdf.addPage(page));
    }

    const mergedBytes = await mergedPdf.save();

    const blob = new Blob([mergedBytes], {
        type: "application/pdf",
    });

    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "worksheets.pdf"; // 👈 THIS is key
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
};
    const filteredMaterials = materials
        .filter((item) => {
            const mat = buildViewMaterial(item);
            return matchesFilters(mat);
        })
        .sort((a, b) => a.id - b.id);

    const visibleFiles = filteredMaterials
        .map((m) => buildViewMaterial(m).file)
        .filter(Boolean);

    const allVisibleSelected =
        visibleFiles.length > 0 &&
        visibleFiles.every((file) =>
            selectedItems.includes(file)
        );

    const visibleFilterOptions = FILTERS.map((option) => {
        const isValid = materials.some((item) => {
            const mat = buildViewMaterial(item);

            const searchMatch = mat.title
                .toLowerCase()
                .includes(searchTerm.toLowerCase());

            const filterMatch =
                activeFilters.length === 0 ||
                activeFilters.every(f => mat.tags.includes(f));

            return (
                searchMatch &&
                filterMatch &&
                mat.tags.includes(option)
            );
        });

        return {
            option,
            isValid,
        };
    });

    return (
        <section
            className="
            w-full
            bg-[#fffaf0]
            pt-0 md:px-12
        "
        >
            <div
                className="
        sticky
        top-0
        pt-[60px]
        z-30
        bg-[#fffaf0]/95
        backdrop-blur-md
        border-b
        border-[#fdcb6e]/20
        mx-0
        sm:px-0 md:px-4
        py-2
    "
            >

                <div className="max-w-6xl mx-auto flex flex-col gap-2 px-5">
                    <div className="flex items-center gap-2">
                        <div className="flex overflow-hidden rounded-xl border border-[#fdcb6e]">
                            <button
                                onClick={() => setViewMode("grid")}
                                className={`h-8 px-2 ${viewMode === "grid"
                                    ? "bg-[#fdcb6e] text-white"
                                    : "hover:bg-[#fdcb6e]/10"
                                    }`}
                            >
                                <Grid3X3 size={20} />
                            </button>

                            <button
                                onClick={() => setViewMode("list")}
                                className={`h-8 px-2 border-l border-[#fdcb6e] ${viewMode === "list"
                                    ? "bg-[#fdcb6e] text-white"
                                    : "hover:bg-[#fdcb6e]/10"
                                    }`}
                            >
                                <List size={20} />
                            </button>
                        </div>

                        <input
                            type="text"
                            placeholder={
                                language === "JP"
                                    ? "プリントを検索..."
                                    : language === "ZH"
                                        ? "搜索练习册..."
                                        : "Search worksheets..."
                            }
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="
                h-8
                px-2
                text-xs
                border
                border-[#fdcb6e]
                rounded-lg
                w-[140px] sm:w-[200px]
            "
                        />
                        <button
                            onClick={downloadSelected}
                            disabled={!selectedItems.length}
                            className="
                h-8
                px-2
                rounded-md
                bg-[#b5435f]
                text-white
                disabled:opacity-50
                text-xs
            "
                        >
                            {language === "JP"
                                ? "ダウンロード"
                                : language === "ZH"
                                    ? "下载"
                                    : "Download"} ({selectedItems.length})
                        </button>
                    </div>
                    <div
                        className="
        flex
        items-center
        gap-2
        overflow-x-auto
        whitespace-nowrap
        py-2
        scrollbar-hide
    "
                    >

                        {/* Filters */}
                        {FILTERS.map((option) => {
                            const isActive = activeFilters.includes(option);

                            return (
                                <button
                                    key={option}
                                    onClick={() => toggleFilter(option)}
                                    className={`
                                        flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition-colors
                                        ${isActive
                                            ? "bg-[#fdcb6e] border-[#fdcb6e] text-black"
                                            : "bg-white border-gray-300 text-gray-600 hover:border-[#fdcb6e]"
                                        }
                                    `}
                                >
                                    <span className={`
                                        w-3.5 h-3.5 flex-shrink-0 rounded-sm border flex items-center justify-center
                                        ${isActive
                                            ? "bg-white border-black/30"
                                            : "border-gray-400 bg-white"
                                        }
                                    `}>
                                        {isActive && (
                                            <svg viewBox="0 0 10 8" className="w-2.5 h-2.5" fill="none">
                                                <path d="M1 4l2.5 2.5L9 1" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </span>
                                    {getFilterLabel(option)}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className="flex justify-end mb-4 px-2">
                <button
                    onClick={selectVisible}
                    className="
            text-sm
            text-[#b5435f]
            hover:underline
        "
                >
                    {allVisibleSelected
                        ? (
                            language === "JP"
                                ? "選択解除"
                                : language === "ZH"
                                    ? "取消全选"
                                    : "Clear Selection"
                        )
                        : (
                            language === "JP"
                                ? "すべて選択"
                                : language === "ZH"
                                    ? "全选"
                                    : "Select All"
                        )}
                </button>
            </div>

            <div
                className={`max-w-6xl mx-auto px-4 ${viewMode === "grid"
                    ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
                    : "flex flex-col gap-2"
                    }`}
            >
                {filteredMaterials.map((item) => {
                    const mat = buildViewMaterial(item);

                    return (
                        <motion.div
                            key={mat.key}
                            id={`worksheet-${mat.key}`}
                            onClick={() => {
    if (!mat.file) return;

    if (selectedItems.length > 0) {
        toggleItem(mat.file);
    } else {
        setPreviewItem(mat);
    }
}}
                            className={`relative ${viewMode === "grid"
                                ? "flex flex-col cursor-pointer"
                                : "flex items-center gap-4 py-3 px-2 md:px-4 cursor-pointer border-b border-[#fdcb6e]/40"
                                }`}
                            initial={{
                                opacity: 0,
                                y: 20,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                        >
                            {mat.file && (
                                <input
                                    type="checkbox"
                                    checked={selectedItems.includes(
                                        mat.file
                                    )}
                                    onChange={() =>
                                        toggleItem(mat.file)
                                    }
                                    onClick={(e) =>
                                        e.stopPropagation()
                                    }
                                    className="
                        absolute
                        top-2
                        left-2
                        w-5
                        h-5
                        z-10
                    "
                                />
                            )}

                            <div
                                className={
                                    viewMode === "list"
                                        ? "w-24 md:w-30 flex-shrink-0"
                                        : ""
                                }
                            >
                                <img
                                    src={mat.image}
                                    alt={mat.title}
                                    loading="lazy"
                                    className={`
                        w-full
                        rounded-sm
                        shadow-xl
                        ${selectedItems.includes(
                                        mat.file
                                    )
                                            ? "ring-4 ring-[#fdcb6e]"
                                            : ""
                                        }
                    `}
                                />
                            </div>

                            <div
                                className={`flex-1 ${viewMode === "list"
                                    ? "flex flex-col md:flex-row md:items-center md:justify-between gap-2"
                                    : "mt-2"
                                    }`}
                            >
                                <div>
                                    <h4 className="font-semibold text-sm">
                                        {mat.title}
                                    </h4>
                                </div>

                                {mat.videoId && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedVideo(
                                                mat.videoId
                                            );
                                        }}
                                        className="
                            group
                            flex items-center gap-2
                            px-2 py-2
                            rounded-full
                            bg-gradient-to-r
                            from-[#ff6b6b]
                            to-[#b5435f]
                            text-white
                            font-semibold
                            text-sm
                            shadow-md
                            hover:shadow-xl
                            hover:scale-105
                            active:scale-95
                            transition-all
                            duration-200
                            ml-auto
                        "
                                    >
                                        <div
                                            className="
                                w-7 h-7
                                rounded-full
                                bg-white
                                flex items-center justify-center
                            "
                                        >
                                            <Play
                                                size={14}
                                                fill="#ff6b6b"
                                                strokeWidth={0}
                                            />
                                        </div>

                                        <span>
                                            {language === "JP"
                                                ? "授業を見る"
                                                : language === "ZH"
                                                    ? "观看课程"
                                                    : "Watch Lesson"}
                                        </span>
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        {/* {previewItem && (
    <div
        className="fixed inset-0 z-50 bg-black/70 p-3 md:p-6"
        onClick={() => setPreviewItem(null)}
    >
        <div className="w-full h-full flex items-center justify-center">
            <div
                className="relative inline-block"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={() => setPreviewItem(null)}
                    className="absolute top-2 right-2 z-10 bg-white/90 hover:bg-white rounded-full px-3 py-1 text-sm shadow"
                >
                    ✕
                </button>

                <img
                    src={previewItem.image}
                    alt={previewItem.title}
                    className="block max-w-[95vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
                />
            </div>
        </div>
    </div>
)} */}
            <VideoModal
                videoId={selectedVideo}
                onClose={() => setSelectedVideo(null)}
            />
        </section >
    );
}